class Carousel {
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            slidesToShow:
                parseInt(element.dataset.slidesToShow) ||
                options.slidesToShow ||
                3,
            autoplay: options.autoplay || false,
            autoplaySpeed: options.autoplaySpeed || 5000,
            ...options,
        };

        this.currentIndex = 0;
        this.isInitialized = false;

        this.init();
    }

    init() {
        this.wrapper = this.element.querySelector(".carousel-wrapper");
        this.trackContainer = this.element.querySelector(
            ".carousel-track-container",
        );
        this.track = this.element.querySelector(".carousel-track");
        this.slides = this.element.querySelectorAll(".carousel-slide");
        this.prevButton = this.element.querySelector(".carousel-arrow--prev");
        this.nextButton = this.element.querySelector(".carousel-arrow--next");

        if (!this.track || this.slides.length === 0) return;

        this.setupCarousel();
        this.bindEvents();

        if (this.options.autoplay) {
            this.startAutoplay();
        }

        this.isInitialized = true;
    }

    setupCarousel() {
        const slidesToShow = this.getSlidesToShow();
        const slideWidth = 100 / slidesToShow;

        // Configuration des slides
        this.slides.forEach((slide) => {
            slide.style.minWidth = `${slideWidth}%`;
            slide.style.flex = `0 0 ${slideWidth}%`;
        });

        // Configuration du track
        this.track.style.display = "flex";
        this.track.style.transition = "transform 0.3s ease";
        this.track.style.flexWrap = "nowrap";

        this.updateNavigation();
        this.updateCarouselVisibility();
    }

    getSlidesToShow() {
        if (window.innerWidth < 768) return 1;
        if (window.innerWidth < 1024)
            return Math.min(2, this.options.slidesToShow);
        return Math.min(this.options.slidesToShow, this.slides.length);
    }

    updateCarouselVisibility() {
        const slidesToShow = this.getSlidesToShow();
        const needsCarousel = this.slides.length > slidesToShow;

        this.wrapper.classList.toggle("carousel-disabled", !needsCarousel);
    }

    bindEvents() {
        if (this.prevButton) {
            this.prevButton.addEventListener("click", () => this.prev());
        }

        if (this.nextButton) {
            this.nextButton.addEventListener("click", () => this.next());
        }

        // Responsive
        let resizeTimer;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.setupCarousel();
                this.goToSlide(Math.min(this.currentIndex, this.getMaxIndex()));
            }, 250);
        });

        // Touch/swipe and drag support
        this.addDragSupport();
    }

    addDragSupport() {
        let startX = 0;
        let currentX = 0;
        let isDragging = false;
        let isMouseDown = false;
        let startTransform = 0;

        // Mouse events (drag)
        this.trackContainer.addEventListener("mousedown", (e) => {
            if (e.button !== 0) return; // Only left click
            e.preventDefault();
            startX = e.clientX;
            isMouseDown = true;
            isDragging = false;
            startTransform = this.getCurrentTransform();
            this.trackContainer.style.cursor = "grabbing";
            this.track.style.transition = "none";
        });

        this.trackContainer.addEventListener("mousemove", (e) => {
            if (!isMouseDown) return;
            e.preventDefault();

            currentX = e.clientX;
            const diff = currentX - startX;

            if (Math.abs(diff) > 5 && !isDragging) {
                isDragging = true;
            }

            if (isDragging) {
                const containerWidth = this.trackContainer.offsetWidth;
                const dragPercentage = (diff / containerWidth) * 100;
                const newTransform = startTransform + dragPercentage;

                // Limiter le drag aux bornes du carousel
                const maxTransform = 0;
                const minTransform = -(
                    this.getMaxIndex() *
                    (100 / this.getSlidesToShow())
                );
                const clampedTransform = Math.max(
                    minTransform,
                    Math.min(maxTransform, newTransform),
                );

                this.track.style.transform = `translateX(${clampedTransform}%)`;
            }
        });

        const handleDragEnd = (e) => {
            if (!isMouseDown) return;

            isMouseDown = false;
            this.trackContainer.style.cursor = "grab";
            this.track.style.transition = "transform 0.3s ease";

            if (isDragging) {
                const endX = e.type === "mouseup" ? e.clientX : currentX;
                const diff = startX - endX;
                const threshold = 50;

                if (Math.abs(diff) > threshold) {
                    if (diff > 0) {
                        this.next();
                    } else {
                        this.prev();
                    }
                } else {
                    // Snap back to current position
                    this.goToSlide(this.currentIndex);
                }

                isDragging = false;
            }
        };

        document.addEventListener("mouseup", handleDragEnd);
        document.addEventListener("mouseleave", handleDragEnd);

        // Touch events (swipe)
        this.trackContainer.addEventListener("touchstart", (e) => {
            startX = e.touches[0].clientX;
            isDragging = false;
            startTransform = this.getCurrentTransform();
            this.track.style.transition = "none";
        });

        this.trackContainer.addEventListener("touchmove", (e) => {
            e.preventDefault();

            currentX = e.touches[0].clientX;
            const diff = currentX - startX;

            if (Math.abs(diff) > 5 && !isDragging) {
                isDragging = true;
            }

            if (isDragging) {
                const containerWidth = this.trackContainer.offsetWidth;
                const dragPercentage = (diff / containerWidth) * 100;
                const newTransform = startTransform + dragPercentage;

                // Limiter le drag aux bornes du carousel
                const maxTransform = 0;
                const minTransform = -(
                    this.getMaxIndex() *
                    (100 / this.getSlidesToShow())
                );
                const clampedTransform = Math.max(
                    minTransform,
                    Math.min(maxTransform, newTransform),
                );

                this.track.style.transform = `translateX(${clampedTransform}%)`;
            }
        });

        this.trackContainer.addEventListener("touchend", (e) => {
            this.track.style.transition = "transform 0.3s ease";

            if (isDragging) {
                const endX = e.changedTouches[0].clientX;
                const diff = startX - endX;
                const threshold = 50;

                if (Math.abs(diff) > threshold) {
                    if (diff > 0) {
                        this.next();
                    } else {
                        this.prev();
                    }
                } else {
                    // Snap back to current position
                    this.goToSlide(this.currentIndex);
                }

                isDragging = false;
            }
        });

        // Prevent default drag behavior on images and links
        this.trackContainer.addEventListener("dragstart", (e) => {
            e.preventDefault();
        });
    }

    getCurrentTransform() {
        const transform = window.getComputedStyle(this.track).transform;
        if (transform === "none") return 0;

        const matrix = new DOMMatrix(transform);
        const containerWidth = this.trackContainer.offsetWidth;
        return (matrix.m41 / containerWidth) * 100;
    }

    prev() {
        if (this.currentIndex > 0) {
            this.goToSlide(this.currentIndex - 1);
        }
    }

    next() {
        const maxIndex = this.getMaxIndex();
        if (this.currentIndex < maxIndex) {
            this.goToSlide(this.currentIndex + 1);
        }
    }

    goToSlide(index) {
        const maxIndex = this.getMaxIndex();
        this.currentIndex = Math.max(0, Math.min(index, maxIndex));

        const slidesToShow = this.getSlidesToShow();
        const slideWidth = 100 / slidesToShow;
        const translateX = -(this.currentIndex * slideWidth);

        this.track.style.transform = `translateX(${translateX}%)`;
        this.updateNavigation();
    }

    getMaxIndex() {
        const slidesToShow = this.getSlidesToShow();
        return Math.max(0, this.slides.length - slidesToShow);
    }

    updateNavigation() {
        if (!this.prevButton || !this.nextButton) return;

        const maxIndex = this.getMaxIndex();

        this.prevButton.disabled = this.currentIndex <= 0;
        this.nextButton.disabled = this.currentIndex >= maxIndex;

        this.prevButton.classList.toggle(
            "carousel-arrow--disabled",
            this.currentIndex <= 0,
        );
        this.nextButton.classList.toggle(
            "carousel-arrow--disabled",
            this.currentIndex >= maxIndex,
        );
    }

    startAutoplay() {
        this.stopAutoplay();
        this.autoplayTimer = setInterval(() => {
            const maxIndex = this.getMaxIndex();
            if (this.currentIndex >= maxIndex) {
                this.goToSlide(0);
            } else {
                this.next();
            }
        }, this.options.autoplaySpeed);
    }

    stopAutoplay() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }

    destroy() {
        this.stopAutoplay();
        // Nettoyer les event listeners si nécessaire
    }
}

// Auto-initialisation
document.addEventListener("DOMContentLoaded", () => {
    const carousels = document.querySelectorAll('[data-carousel="true"]');
    carousels.forEach((carousel) => {
        new Carousel(carousel);
    });
});

// Export pour utilisation manuelle
window.Carousel = Carousel;
